import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import NavigationBar from './components/NavigationBar';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectInsightsPage from './pages/ProjectInsightsPage';
import GenerateSpecPage from './pages/GenerateSpecPage';
import ViewDocumentPage from './pages/ViewDocumentPage';
import DocumentationHomePage from './pages/DocumentationHomePage';
import AdminPanel from './pages/AdminPanel'; // Import the Admin Panel component
import SolutionProposalPage from './pages/SolutionProposalPage'; // Import the Solution Proposal Page
import TechnicalSpecificationPage from './pages/TechnicalSpecificationPage'; // Import the Technical Specification Page
import ViewTechnicalSpecPage from './pages/ViewTechnicalSpecPage'; // Import the View Technical Specification Page
import SettingsPage from './pages/SettingsPage'; // Import the Settings Page
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { JobProvider } from './contexts/JobContext';
import JobStatusBar from './components/JobStatusBar';

// Import ProjectOverview components
import ProjectOverviewLayout from './pages/ProjectOverview/ProjectOverviewLayout';
import SOWInsightsPage from './pages/ProjectOverview/SOWInsightsPage';
import ProjectIntelligencePage from './pages/ProjectOverview/ProjectIntelligencePage';

// Import PreparePhase components
import PreparePhaseLayout from './pages/PreparePhase/PreparePhaseLayout';
import ProjectScopePage from './pages/PreparePhase/ProjectScopePage';
import BusinessRequirementsPage from './pages/PreparePhase/BusinessRequirementsPage';
import TechnicalRequirementsPage from './pages/PreparePhase/TechnicalRequirementsPage';
import IntegrationMapPage from './pages/PreparePhase/IntegrationMapPage';
import StakeholderAnalysisPage from './pages/PreparePhase/StakeholderAnalysisPage';
import ProjectTimelinePage from './pages/PreparePhase/ProjectTimelinePage';
import WorkshopSchedulePage from './pages/PreparePhase/WorkshopSchedulePage';
import RACIMatrixPage from './pages/PreparePhase/RACIMatrixPage';
import TemplatesPage from './pages/PreparePhase/TemplatesPage';

// Import DiscoverPhase components
import DiscoveryPhaseLayout from './pages/DiscoveryPhase/DiscoveryPhaseLayout';
import BusinessAssessmentPage from './pages/DiscoveryPhase/BusinessAssessmentPage';
import CurrentLandscapeAnalysisPage from './pages/DiscoveryPhase/CurrentLandscapeAnalysisPage';
import RequirementsGatheringPage from './pages/DiscoveryPhase/RequirementsGatheringPage';
import StakeholderInterviewsPage from './pages/DiscoveryPhase/StakeholderInterviewsPage';
import TechnologyAssessmentPage from './pages/DiscoveryPhase/TechnologyAssessmentPage';
import FeasibilityStudyPage from './pages/DiscoveryPhase/FeasibilityStudyPage';
import RFPAnalysisPage from './pages/DiscoveryPhase/RFPAnalysisPage';

// Import ExplorePhase components
import ExplorePhaseLayout from './pages/ExplorePhase/ExplorePhaseLayout';
import BPMLPage from './pages/ExplorePhase/BPMLPage';
import FitToStandardPage from './pages/ExplorePhase/FitToStandardPage';
import GapAnalysisPage from './pages/ExplorePhase/GapAnalysisPage';

// Import RealizePhase components
import RealizePhaseLayout from './pages/RealizePhase/RealizePhaseLayout';
import UATTestScriptsPage from './pages/RealizePhase/UATTestScriptsPage';
import WorkInProgressPage from './pages/RealizePhase/WorkInProgressPage';
import RICEFWListPage from './pages/RealizePhase/RICEFWListPage';

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#F5821F', // Orange color from Selectivate logo
    },
    secondary: {
      main: '#1D2B3F', // Dark blue from Selectivate logo
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

// Conditional NavigationBar component - only show when authenticated
const ConditionalNavBar = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Hide only on public pages (login, register, forgot-password, reset-password)
  const publicPaths = ['/', '/login', '/auth/callback', '/register', '/forgot-password', '/reset-password'];
  const isPublicPath = publicPaths.some(path => location.pathname === path);

  if (!isAuthenticated || isPublicPath) {
    return null;
  }

  return <NavigationBar />;
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    // Return loading spinner or placeholder
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return children;
};

// Admin route component - only accessible by admins
const AdminRoute = ({ children }) => {
  const { currentUser, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    // Return loading spinner or placeholder
    return <div>Loading...</div>;
  }

  if (!isAuthenticated || (currentUser && currentUser.role !== 'admin' && currentUser.role !== 'Administrator')) {
    // Redirect non-admin users to the home page
    return <Navigate to="/home" />;
  }

  return children;
};

function App() {
  useEffect(() => {
    // Set Selectivate favicon dynamically
    const favicon = document.getElementById('favicon');
    if (favicon) {
      favicon.href = '/favicon.ico';
    }
    
    // Set page title
    document.title = 'Selectivate - SAP Documentation Platform';
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <JobProvider>
      <AuthProvider>
        <Router>
          <ConditionalNavBar />
          <JobStatusBar />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Protected routes */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documentation"
              element={
                <ProtectedRoute>
                  <DocumentationHomePage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/projects" 
              element={
                <AdminRoute>
                  <ProjectsPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/projects/:projectId" 
              element={
                <AdminRoute>
                  <ProjectDetailPage />
                </AdminRoute>
              } 
            />
            <Route
              path="/projects/:projectId/insights"
              element={
                <AdminRoute>
                  <ProjectInsightsPage />
                </AdminRoute>
              }
            />

            {/* ProjectOverview Routes - Foundation before SAP Phases */}
            <Route
              path="/projects/:projectId/overview"
              element={
                <AdminRoute>
                  <ProjectOverviewLayout />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="sow-insights" replace />} />
              <Route path="sow-insights" element={<SOWInsightsPage />} />
              <Route path="scope" element={<ProjectScopePage />} />
              <Route path="requirements" element={<BusinessRequirementsPage />} />
              <Route path="stakeholders" element={<StakeholderAnalysisPage />} />
              <Route path="timeline" element={<ProjectTimelinePage />} />
              <Route path="intelligence" element={<ProjectIntelligencePage />} />
            </Route>

            {/* DiscoverPhase Routes */}
            <Route 
              path="/projects/:projectId/discovery" 
              element={
                <AdminRoute>
                  <DiscoveryPhaseLayout />
                </AdminRoute>
              } 
            >
              <Route path="rfp-analysis" element={<RFPAnalysisPage />} />
              <Route path="business-assessment" element={<BusinessAssessmentPage />} />
              <Route path="current-landscape-analysis" element={<CurrentLandscapeAnalysisPage />} />
              <Route path="requirements-gathering" element={<RequirementsGatheringPage />} />
              <Route path="stakeholder-interviews" element={<StakeholderInterviewsPage />} />
              <Route path="technology-assessment" element={<TechnologyAssessmentPage />} />
              <Route path="feasibility-study" element={<FeasibilityStudyPage />} />
              {/* Add other discovery phase routes here when pages are created */}
            </Route>
            
            {/* PreparePhase Routes - Detailed execution phase (after overview) */}
            <Route
              path="/projects/:projectId/prepare"
              element={
                <AdminRoute>
                  <PreparePhaseLayout />
                </AdminRoute>
              }
            >
              <Route path="technical-requirements" element={<TechnicalRequirementsPage />} />
              <Route path="integration-map" element={<IntegrationMapPage />} />
              <Route path="workshop-schedule" element={<WorkshopSchedulePage />} />
              <Route path="raci-matrix" element={<RACIMatrixPage />} />
              <Route path="templates" element={<TemplatesPage />} />
            </Route>

            {/* ExplorePhase Routes - Business Process Discovery */}
            <Route
              path="/projects/:projectId/explore"
              element={
                <AdminRoute>
                  <ExplorePhaseLayout />
                </AdminRoute>
              }
            >
              <Route path="bpml-bpd" element={<BPMLPage />} />
              <Route path="fit-to-standard" element={<FitToStandardPage />} />
              <Route path="gap-analysis" element={<GapAnalysisPage />} />
            </Route>

            {/* RealizePhase Routes - Solution Build & Implementation (accessible to any authenticated user) */}
            <Route
              path="/projects/:projectId/realize"
              element={
                <ProtectedRoute>
                  <RealizePhaseLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="ricefw-list" replace />} />
              <Route path="functional-specs" element={<GenerateSpecPage />} />
              <Route path="technical-specs" element={<WorkInProgressPage title="Technical Specs" />} />
              <Route path="configuration" element={<WorkInProgressPage title="Configuration" />} />
              <Route path="development" element={<WorkInProgressPage title="Development" />} />
              <Route path="uat-test-scripts" element={<UATTestScriptsPage />} />
              <Route path="data-migration" element={<WorkInProgressPage title="Data Migration" />} />
              <Route path="ricefw-list" element={<RICEFWListPage />} />
            </Route>

            <Route 
              path="/projects/:projectId/solution-proposal" 
              element={
                <AdminRoute>
                  <SolutionProposalPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/projects/:projectId/technical-specification" 
              element={
                <AdminRoute>
                  <TechnicalSpecificationPage />
                </AdminRoute>
              } 
            />
            <Route 
              path="/generate" 
              element={
                <ProtectedRoute>
                  <GenerateSpecPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/view-document" 
              element={
                <ProtectedRoute>
                  <ViewDocumentPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/view-technical-spec" 
              element={
                <ProtectedRoute>
                  <ViewTechnicalSpecPage />
                </ProtectedRoute>
              } 
            />
            {/* Admin Panel Route - Only accessible by admins */}
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              } 
            />
            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/home" />} />
          </Routes>
        </Router>
      </AuthProvider>
      </JobProvider>
    </ThemeProvider>
  );
}

export default App;
